import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";
import ReportProblem from "./components/ReportProblem";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Billing from "./pages/Billing";
import BillingSuccess from "./pages/BillingSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SupportInbox from "./pages/SupportInbox";
import Sessions from "./pages/Sessions";
import Marketing from "./pages/Marketing";
import AdminUsers from "./pages/AdminUsers";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import Brand from "./pages/Brand";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PaywallGate({ children }) {
  // Authed AND has active subscription — otherwise redirect to billing
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.has_active_subscription) return <Navigate to="/billing" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={user.has_active_subscription ? "/app" : "/billing"} replace />;
  }
  return children;
}

// Show the floating "Report a problem" launcher only when a user is signed in.
function AuthedFloatingActions() {
  const { user } = useAuth();
  if (!user) return null;
  return <ReportProblem />;
}

// Public marketing site for logged-out visitors; authed users hit the app.
function MarketingOrApp() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={user.has_active_subscription ? "/app" : "/billing"} replace />;
  }
  return <Marketing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <InstallPrompt />
          <AuthedFloatingActions />
          <Routes>
            <Route path="/" element={<MarketingOrApp />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/app" element={<PaywallGate><Dashboard /></PaywallGate>} />
            <Route path="/support" element={<Protected><SupportInbox /></Protected>} />
            <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
            <Route path="/account/sessions" element={<Protected><Sessions /></Protected>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/brand" element={<Brand />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
